import firebase from '@firebase/app'
import '@firebase/database'
import Vue from 'vue'
import slugid from 'slugid'
import {
  cleanFirebaseObject,
  getDomain,
  getImage } from '../utils'
import { Recipe } from '../models/Recipe'
import {
  FINISH_LOADING,
  SET_ITEMS,
  ADD_ITEM,
  DELETE_ITEM,
  UPDATE_ITEM,
  CLEAR_RECIPES,
  REMOVE_RECIPE_IMAGE,
  ADD_COLLECTION_TO_RECIPE,
  REMOVE_COLLECTION_FROM_RECIPE,
  SET_LOCAL_RECIPE_IMAGE_URL,
  ADD_NOTIFICATION,
  SET_SCALE,
  RESET_SCALE } from './mutation-types'

const extensionsByContentType = {
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png'
}
const fetchCount = 50
const getDatabaseRef = (userId, recipeId) => {
  return firebase.database().ref(`users/${userId}/recipes/${recipeId}`)
}
const getStorageRef = (userId, filename) => {
  return firebase.storage().ref().child(`users/${userId}/images/recipes/${filename}`)
}
const sortByAddedAt = (a, b) => {
  return (b.addedAt || -Infinity) - (a.addedAt || -Infinity)
}
const weightMatches = (match) => {
  if (match.nameMatch) {
    return 10
  }
  if (match.urlMatch) {
    return 9
  }
  if (match.ingredientMatch) {
    return 6
  }
  if (match.instructionMatch) {
    return 3
  }
  return 0
}
const sortSearchMatches = (a, b) => {
  const weightsA = weightMatches(a)
  const weightsB = weightMatches(b)
  if (weightsA === weightsB) {
    return b.addedAt > a.addedAt ? -1 : 1
  }
  return weightsB - weightsA
}
const sortByCollectionAddedAt = (a, b, collectionId) => {
  try {
    return b.collections[collectionId] - a.collections[collectionId]
  } catch (err) {
    return -Infinity
  }
}
const buildChangeObject = (before = [], after = []) => {
  let changes = {}
  const changeStub = () => {
    return { before: null, after: null }
  }
  before.forEach(item => {
    if (!changes[item.key]) changes[item.key] = changeStub()
    changes[item.key].before = item
  })
  after.forEach(item => {
    if (!changes[item.key]) changes[item.key] = changeStub()
    changes[item.key].after = item
  })
  return changes
}

const getters = {
  allRecipes: state => () => {
    return Object.values(state.items).sort(sortByAddedAt)
  },
  collectionRecipes: (state, getters, rootState) => (collectionId, sort) => {
    let collection = rootState.Collection.collections[collectionId]
    if (!collection) return []
    let collectionRecipeIds = Object.keys(collection.items)
    return Object.values(state.items).filter(recipe => {
      return collectionRecipeIds.indexOf(recipe.key) !== -1
    }).sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.name.localeCompare(b.name)
        default: // 'recent'
          if (collectionId === 'library') {
            return sortByAddedAt(a, b)
          } else {
            return sortByCollectionAddedAt(a, b, collectionId)
          }
      }
    })
  },
  getRecipeById: state => (recipeId) => {
    return state.items[recipeId]
  },
  searchedRecipes: state => (query) => {
    if (!query) return getters.allRecipes(state)()
    var re = new RegExp(query, 'i')
    return Object.values(state.items).map(recipe => ({
      recipe,
      addedAt: recipe.addedAt || 0,
      nameMatch: recipe.name ? recipe.name.match(re) : false,
      urlMatch: recipe.url ? getDomain(recipe.url).match(re) : false,
      ingredientMatch: recipe.ingredients.find(i => i.text.match(re)),
      instructionMatch: recipe.instructions.find(i => i.text.match(re))
    }))
      .filter(m => m.nameMatch || m.urlMatch || m.ingredientMatch || m.instructionMatch)
      .sort(sortSearchMatches)
      .map(m => m.recipe)
  },
  recentlyAddedRecipes: (state, getters, rootState) => () => {
    return getters.allRecipes().slice(0, 20)
  },
  uncategorizedRecipes: (state, getters) => (sort) => {
    return Object.values(state.items).filter(recipe => {
      return Object.keys(recipe.collections || {}).length === 0
    }).sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.name.localeCompare(b.name)
        default: // 'recent'
          return sortByAddedAt(a, b)
      }
    })
  }
}
const getDefaultState = () => {
  return {
    current: null,
    items: {},
    loading: true,
    local: {}
  }
}

export default {
  state: getDefaultState(),
  getters,
  mutations: {
    [SET_ITEMS] (state, { items }) {
      let recipes = Object.entries(items).reduce((recipes, [key, item]) => {
        recipes[key] = new Recipe(item, key)
        return recipes
      }, {})

      state.items = { ...state.items, ...recipes }
      if (state.loading) state.loading = false
    },
    [ADD_ITEM] (state, { key, data }) {
      // Organise data for presentation
      Vue.set(state.items, key, new Recipe(data, key))
    },
    [DELETE_ITEM] (state, { key }) {
      Vue.delete(state.items, key)
    },
    [FINISH_LOADING] (state, payload) {
      state.loading = false
    },
    [UPDATE_ITEM] (state, { key, data }) {
      // Checks if an item has been deleted and stops running
      let item = state.items[key]
      if (!item) return
      let mergedData = Object.assign(data, {
        localImageUrl: item.localImageUrl
      })

      let recipe = new Recipe(mergedData, key)
      Vue.set(state.items, key, recipe)
    },
    [CLEAR_RECIPES] (state) {
      Object.assign(state, getDefaultState())
    },
    [ADD_COLLECTION_TO_RECIPE] (state, { key, collectionKey }) {
      let recipe = state.items[key]
      let obj = {}
      obj[collectionKey] = +new Date()
      Vue.set(recipe, 'collections', Object.assign({}, recipe.collections, obj))
    },
    [REMOVE_COLLECTION_FROM_RECIPE] (state, { key, collectionKey }) {
      if (!state.items[key]) return
      if (collectionKey === 'library') return
      Vue.delete(state.items[key].collections, collectionKey)
    },
    [REMOVE_RECIPE_IMAGE] (state, { recipeKey }) {
      Vue.delete(state.items[recipeKey], 'image')
      Vue.delete(state.items[recipeKey], 'localImageUrl')
    },
    [SET_LOCAL_RECIPE_IMAGE_URL] (state, { recipeKey, localUrl }) {
      Vue.set(state.items[recipeKey], 'localImageUrl', localUrl)
    },
    [SET_SCALE] (state, { recipeKey, scale }) {
      Vue.set(state.local, recipeKey, { scale })
    },
    [RESET_SCALE] (state, { recipeKey }) {
      Vue.set(state.local, recipeKey, { scale: '1' })
    }
  },
  actions: {
    fetchRecipes ({commit, dispatch}, {key, endAt}) {
      const path = `users/${key}/recipes`

      let ref = firebase.database()
        .ref(path)
        .orderByChild('addedAt')
        .limitToLast(fetchCount)
      if (endAt) ref = ref.endAt(endAt)

      ref.once('value')
        .then(snapshot => {
          let recipes = snapshot.val()
          if (!snapshot.exists()) return commit(FINISH_LOADING)
          let recipeKeys = Object.keys(recipes)
          let firstAddedAt = recipes[recipeKeys[0]].addedAt

          commit(SET_ITEMS, { items: recipes })
          setTimeout(() => {
            if (endAt !== firstAddedAt) {
              dispatch('fetchRecipes', {
                key,
                endAt: firstAddedAt
              })
            }
          }, 2)
        })
        .catch(error => { // eslint-disable-line
          console.log(error)
          commit(ADD_NOTIFICATION, {
            message: 'Unable to load recipes'
          })
        })
    },
    attachRecipeListeners ({commit, dispatch, state}, {key}) {
      let ref = firebase.database().ref(`users/${key}/recipes`)

      ref.limitToLast(1)
        .orderByChild('addedAt')
        .on('child_added', snapshot => {
          commit(ADD_ITEM, {
            key: snapshot.key,
            data: snapshot.val()
          })
        })

      ref.on('child_changed', snapshot => {
        commit(UPDATE_ITEM, {
          key: snapshot.key,
          data: snapshot.val()
        })
      })

      ref.on('child_removed', snapshot => {
        commit(DELETE_ITEM, { key: snapshot.key })
      })
    },
    deleteRecipe ({commit, state, dispatch}, {uid, key}) {
      return new Promise((resolve, reject) => {
        commit(DELETE_ITEM, { key })
        firebase.database()
          .ref(`users/${uid}/recipes/${key}`)
          .remove()
          .then(() => {
            commit(ADD_NOTIFICATION, {
              message: 'Deleted recipe'
            })
            resolve()
          }).catch(error => {
            commit(ADD_NOTIFICATION, {
              message: 'Unable to delete recipe'
            })
            reject(error)
          })
      })
    },
    updateRecipe ({commit, state}, {uid, key, updatedRecipe, callback}) {
      let currentRecipe = state.items[key]
      let updates = {}

      if (updatedRecipe.hasOwnProperty('ingredients')) {
        let ops = buildChangeObject(
          currentRecipe.ingredients,
          updatedRecipe.ingredients
        )
        Object.keys(ops).forEach(key => {
          let op = ops[key]
          if (!op.after || op.after.text === '') {
            // Ingredient removed or is blank
            updates[`/ingredients/${key}`] = null
          } else if (!op.before || op.before.text !== op.after.text) {
            // Ingredient added OR updated
            updates[`/ingredients/${key}`] = {
              priority: op.after.priority,
              section: op.after.section || null,
              text: op.after.text
            }
          } else if (op.before.priority !== op.after.priority) {
            // Ingredient priority changed
            updates[`/ingredients/${key}/priority`] = op.after.priority
          }
        })
      }

      // TODO:
      // - Utilise the change object above
      let uInstructions = updatedRecipe.instructions || {}
      if (Object.keys(uInstructions).length > 0) {
        Object.keys(uInstructions).forEach(k => {
          let uInstruction = uInstructions[k]
          uInstruction.text = uInstruction.text.trim()
          if (!uInstruction.text) return delete uInstructions[k]
        })
        updates['/instructions'] = uInstructions
      }

      if (Array.isArray(updatedRecipe.notes)) {
        updatedRecipe.notes = updatedRecipe.notes.join('\n')
      }

      let attributes = ['name', 'description', 'prepTime', 'cookTime', 'totalTime', 'recipeYield', 'notes']
      attributes.forEach(attribute => {
        let before = currentRecipe[attribute] || null
        let after = updatedRecipe[attribute]
        if (after !== undefined && before !== after) {
          updates[`/${attribute}`] = after === '' ? null : after
        }
      })

      if (Object.keys(updates).length === 0) {
        if (callback) callback()
        return
      }

      const ref = `users/${uid}/recipes/${key}`
      firebase.database()
        .ref(ref)
        .update(updates, error => {
          if (error) {
            console.log(error)
          } else {
            if (callback) callback()
          }
        })
    },
    createRecipe ({commit, state, dispatch}, {userId, recipe, collectionId}) {
      recipe = cleanFirebaseObject(recipe)
      let ref = firebase.database().ref(`users/${userId}/recipes`).push()
      recipe.addedAt = firebase.database.ServerValue.TIMESTAMP
      recipe.application = {
        name: 'web'
      }

      return new Promise((resolve, reject) => {
        ref.set(recipe).then(() => {
          if (collectionId) {
            dispatch({
              type: 'addToCollection',
              uid: userId,
              key: ref.key,
              collectionKey: collectionId
            })
          }
          resolve(ref.key)
        }).catch((error) => {
          commit(ADD_NOTIFICATION, {
            message: 'Unable to add recipe'
          })
          console.log(error)
          reject(error)
        })
      })
    },
    uploadImage ({dispatch, commit, state}, {userId, recipeKey, file}) {
      return import(/* webpackChunkName: "firebase-storage" */ '@firebase/storage').then(() => {
        const contentType = file.type
        const extname = extensionsByContentType[contentType]
        if (!extname) return Promise.reject(new Error('Only images may be uploaded'))

        window.URL = window.URL || window.webkitURL
        const localUrl = window.URL.createObjectURL(file)
        const fileroot = slugid.nice()
        const filename = `${fileroot}.${extname}`
        const storageRef = getStorageRef(userId, filename)
        const recipeRef = getDatabaseRef(userId, recipeKey)

        return getImage(localUrl).then(image => {
          commit(SET_LOCAL_RECIPE_IMAGE_URL, {
            recipeKey: recipeKey,
            localUrl
          })
          return {
            contentType,
            filename,
            height: image.naturalHeight || image.height,
            state: 'loaded',
            width: image.naturalWidth || image.width
          }
        }).then(imageObj => {
          return storageRef.put(file).then((snapshot) => {
            imageObj.sourceUrl = snapshot.downloadURL
            imageObj.state = 'uploaded'
            return Promise.resolve(imageObj)
          })
        }).then(imageObj => {
          return recipeRef.child('image').set(imageObj)
        })
      }).catch(error => { //eslint-disable-line
        commit(ADD_NOTIFICATION, {
          message: 'Unable to upload image'
        })
      })
    },
    removeRecipeImage ({commit, state}, {userId, recipeKey}) {
      const path = `users/${userId}/recipes/${recipeKey}/image`
      commit(REMOVE_RECIPE_IMAGE, { recipeKey })
      return new Promise((resolve, reject) => {
        firebase.database()
          .ref(path)
          .remove()
          .then(() => {
            resolve()
          }).catch(error => {
            commit(ADD_NOTIFICATION, {
              message: 'Unable to remove image'
            })
            reject(error)
          })
      })
    },
    tagRecipe ({commit, state}, {userId, recipeKey}) {
      const path = `users/${userId}/recipes/${recipeKey}/_triggers/tag`
      return new Promise((resolve, reject) => {
        firebase.database()
          .ref(path)
          .set(true, () => {
            resolve()
          }).catch(error => {
            commit(ADD_NOTIFICATION, {
              message: 'Unable to tag recipe'
            })
            reject(error)
          })
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/modules/Recipe.js